import type { ServiceRouter } from "@fideus/service-api";
import { registerTransformers } from "@fideus/temporal-superjson-transformers";
import { registerTaxIdTransformer } from "@fideus-core/company";
import { registerLoanAgreementTransformer } from "@fideus-core/loan-agreement";
import { registerAmountTransformer } from "@fideus-core/transaction";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { type ReactNode, useState } from "react";
import superjson from "superjson";

// Register all core transformers on the client
registerTransformers(
  superjson,
  registerAmountTransformer,
  registerTaxIdTransformer,
  registerLoanAgreementTransformer
);

import { useGetToken, useRedirectToAuth } from "@fideus/auth";
import { AuthRedirectEvent, getPosthog, PosthogAnalytics } from "../analytics";
import { isAuthError } from "./isAuthError";
import { TRPCFetchError } from "./TRPCFetchError";
import { TRPCTimeoutError } from "./TRPCTimeoutError";
import { TRPCProvider } from "./TrpcContext";

interface TrpcApiProviderProps {
  apiUrl: string;
  children: ReactNode;
}

async function handleFetchResponse(
  response: Response,
  url: URL | RequestInfo,
  redirectToAuth: (opts: { redirectBackTo: URL }) => Promise<void>
): Promise<Response> {
  if (response.status === 401 || response.status === 403) {
    const posthog = getPosthog();
    if (posthog) {
      new PosthogAnalytics(posthog, { userType: "service" }).track(
        new AuthRedirectEvent({
          status: response.status,
          url: String(url),
        })
      );
    }
    await redirectToAuth({
      redirectBackTo: new URL(window.location.href),
    });
    // Browser is redirecting to auth — return a pending promise to avoid
    // throwing an error that surfaces as unhandled in error tracking.
    return new Promise<Response>(() => {});
  }
  if (response.status === 504) {
    throw new TRPCTimeoutError({ url, status: response.status });
  }
  return response;
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000 /* ms */,
        throwOnError: (error) => !isAuthError(error),
      },
      mutations: {
        throwOnError: (error) => !isAuthError(error),
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function TrpcApiProvider({ apiUrl, children }: TrpcApiProviderProps) {
  const getToken = useGetToken();
  const redirectToAuth = useRedirectToAuth();

  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<ServiceRouter>({
      links: [
        httpBatchLink({
          url: apiUrl,
          async fetch(url: URL | RequestInfo, options?: RequestInit) {
            try {
              const response = await fetch(url, {
                ...options,
                credentials: "include",
              });
              return await handleFetchResponse(response, url, redirectToAuth);
            } catch (error) {
              if (error instanceof TRPCTimeoutError) {
                throw error;
              }
              throw new TRPCFetchError({ error, url, options });
            }
          },
          transformer: superjson,
          headers: async () => {
            try {
              const token = await getToken();
              return {
                Authorization: `Bearer ${token}`,
              };
            } catch {
              // Token retrieval failed (e.g., session expired) - redirect to re-authenticate
              await redirectToAuth({
                redirectBackTo: new URL(window.location.href),
              });
              // Return empty headers - redirect will navigate away
              return {};
            }
          },
        }),
      ],
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
